package one.devsbox.www.ui.users

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import compose.assets.Svg
import compose.assets.svg.Plus
import compose.assets.svg.UserOutline
import one.devsbox.shared.ads.SharedIO
import one.devsbox.www.io.Repository
import one.devsbox.www.ui.widgets.CategorySplashUI
import one.devsbox.www.ui.widgets.ContentActions
import one.devsbox.www.ui.widgets.FullscreenErrorUI
import one.devsbox.www.ui.widgets.FullscreenLoadingUI
import one.devsbox.www.ui.widgets.LabelField
import one.devsbox.www.ui.widgets.SearchBox
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState

@Composable
@ComposableNavNode
fun ListingUsersUI() {
    with(ListingUsersCtx.get()) {
        ContentListingUsersUI()
    }
}

@Composable
context(ctx: ListingUsersCtx)
private fun ContentListingUsersUI() {
    Box(modifier = Modifier.fillMaxSize()) {
        Crossfade(targetState = ctx.vm.state) { state ->
            when (state) {
                is DataState.Idle -> Unit
                is DataState.Loading -> FullscreenLoadingUI()
                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = state.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reload() }
                    )
                }

                is DataState.Result -> Box(modifier = Modifier.fillMaxSize()) {
                    val resultData = state.nnvl
                    if (resultData.count == 0) {
                        CategorySplashUI(
                            icon = Svg.UserOutline,
                            title = Res.string.ui_users_splash_title,
                            explanation = Res.string.ui_users_splash_desc,
                            actions = ctx.vm.contentActions
                        )
                    } else {
                        SuccessUI(resultData)
                        ContentActions(
                            contentActions = ctx.vm.contentActions,
                            colorInactive = ctx.colorSecondaryContainer,
                            colorActive = ctx.colorPrimaryContainer
                        )
                    }
                }
            }
        }
    }
}

@Composable
context(ctx: ListingUsersCtx)
private fun SuccessUI(data: SharedIO.Users) {
    val filteredItems = data.items.filter {
        it.name.contains(ctx.vm.search.text, ignoreCase = true)
    }.sortedByDescending { it.id }

    Column(modifier = Modifier.fillMaxSize().padding(16.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            SearchBox(
                state = ctx.vm.search,
                hint = stringResource(Res.string.ui_users_listing_search_placeholder),
                modifier = Modifier.weight(1f),
            )
        }

        Spacer(Modifier.height(16.dp))

        val showingStart = if (filteredItems.isEmpty()) 0 else 1
        val showingEnd = filteredItems.size
        val totalCount = data.count

        Text(
            text = stringResource(
                Res.string.ui_users_listing_showing,
                showingStart,
                showingEnd,
                totalCount
            ),
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f),
            letterSpacing = 1.sp,
            modifier = Modifier.padding(bottom = 16.dp)
        )

        if (filteredItems.isEmpty()) {
            Box(
                modifier = Modifier.fillMaxWidth().weight(1f),
                contentAlignment = Alignment.Center
            ) {
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    Icon(
                        imageVector = Svg.UserOutline,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f),
                        modifier = Modifier.size(48.dp)
                    )
                    Spacer(Modifier.height(16.dp))
                    Text(
                        text = stringResource(Res.string.ui_users_listing_no_users_found),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f)
                    )
                }
            }
        } else {
            // Table Header
            Row(
                modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp),
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                LabelField(text = stringResource(Res.string.ui_users_listing_header_identity))
                LabelField(text = stringResource(Res.string.ui_users_listing_header_activity))
            }
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)

            LazyColumn(
                modifier = Modifier.weight(1f).fillMaxWidth(),
                contentPadding = PaddingValues(bottom = 250.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                item {
                    Spacer(Modifier.height(8.dp))
                }

                items(filteredItems) { user ->
                    UserItem(user) {
                        ctx.action.openDetail(user.id)
                    }
                }
                if (data.items.size < data.count) {
                    item {
                        FooterSection()
                    }
                }
            }
        }
    }
}

@Composable
context(ctx: ListingUsersCtx)
private fun UserItem(user: SharedIO.UserSimple, onClick: () -> Unit) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .border(
                width = 1.dp,
                color = MaterialTheme.colorScheme.outlineVariant,
                shape = RoundedCornerShape(4.dp)
            )
            .clip(RoundedCornerShape(4.dp))
            .clickable(onClick = onClick)
            .background(Color.Transparent)
            .padding(horizontal = 16.dp, vertical = 16.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column {
                Text(
                    text = user.name.uppercase(),
                    style = MaterialTheme.typography.bodyLarge,
                    fontWeight = FontWeight.Bold
                )
                if (ctx.me.info.adminMode) {
                    Text(
                        text = user.nameCompany,
                        style = MaterialTheme.typography.labelMedium,
                        color = MaterialTheme.colorScheme.secondary,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }

            Text(
                text = user.lastActivity(),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

@Composable
context(ctx: ListingUsersCtx)
private fun FooterSection() {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 32.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {

        if (ctx.vm.loadingMore) {
            CircularProgressIndicator(modifier = Modifier.size(24.dp))
        } else {
            OutlinedButton(
                onClick = { ctx.vm.action.loadMore() },
                shape = RoundedCornerShape(4.dp),
                colors = ButtonDefaults.outlinedButtonColors(
                    contentColor = MaterialTheme.colorScheme.primary
                ),
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
                modifier = Modifier.width(200.dp).height(48.dp)
            ) {
                Text(
                    stringResource(Res.string.ui_users_listing_btn_load_more),
                    fontWeight = FontWeight.Bold
                )
            }
        }

    }
}
